import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { createStorePayment, getStorePaymentStatus, initiateStorePayment } from "../../services/api"; // adjust path
import { billFromResponse, makeRef, normalizeBillStatus } from "../../utils/storeBill"; // adjust path

const msg = (e, fallback) => e?.message || fallback;

/* ── store side: POST /store-pay/create ── */
export const createStoreBill = createAsyncThunk("storeBill/create", async ({ storeId, amount }, { rejectWithValue }) => {
    try {
        const res = await createStorePayment({ store_id: storeId, amount, reference: makeRef() });
        if (!res?.payment || !res?.qr?.ref) throw new Error(res?.message || "Unable to create the bill. Please try again.");
        return billFromResponse(res);
    } catch (e) {
        return rejectWithValue(msg(e, "Unable to create the bill. Please try again."));
    }
});

/* ── store side: GET /store-pay/status/{ref} ── arg: { ref, silent? } (silent = background poll, no spinner) */
export const fetchStoreBillStatus = createAsyncThunk("storeBill/status", async ({ ref }, { rejectWithValue }) => {
    try {
        const res = await getStorePaymentStatus(ref);
        return { ref, status: normalizeBillStatus(res) };
    } catch (e) {
        return rejectWithValue(msg(e, "Unable to check the payment status."));
    }
});

/* ── customer side: POST /store-pay/initiate ── arg: ref */
export const initiateStoreBill = createAsyncThunk("storeBill/initiate", async (ref, { rejectWithValue }) => {
    try {
        await initiateStorePayment(ref);
        return { ref };
    } catch (e) {
        return rejectWithValue(msg(e, "Unable to start the payment. Please try again."));
    }
});

const initialState = {
    // store side
    bill: null, // { id, ref, storeId, amount, createdAt, expiresAt, invoice, phone }
    status: "pending", // pending | initiated | paid | cancelled | expired
    creating: false,
    createError: null,
    checking: false,
    lastChecked: null,
    // customer side
    initiating: false,
};

const storeBillSlice = createSlice({
    name: "storeBill",
    initialState,
    reducers: {
        // back to the form (also used for "Create new bill")
        clearStoreBill: (state) => {
            state.bill = null;
            state.status = "pending";
            state.createError = null;
            state.checking = false;
            state.lastChecked = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // create
            .addCase(createStoreBill.pending, (state) => {
                state.creating = true;
                state.createError = null;
            })
            .addCase(createStoreBill.fulfilled, (state, action) => {
                state.creating = false;
                state.bill = action.payload;
                state.status = "pending";
                state.lastChecked = null;
            })
            .addCase(createStoreBill.rejected, (state, action) => {
                state.creating = false;
                state.createError = action.payload || "Unable to create the bill. Please try again.";
            })
            // status
            .addCase(fetchStoreBillStatus.pending, (state, action) => {
                if (!action.meta.arg.silent) state.checking = true;
            })
            .addCase(fetchStoreBillStatus.fulfilled, (state, action) => {
                state.checking = false;
                if (state.bill?.ref !== action.payload.ref) return; // stale response for an older QR
                state.lastChecked = Date.now();
                if (state.status !== "paid") state.status = action.payload.status; // paid is final
            })
            .addCase(fetchStoreBillStatus.rejected, (state) => {
                state.checking = false;
            })
            // initiate (customer)
            .addCase(initiateStoreBill.pending, (state) => {
                state.initiating = true;
            })
            .addCase(initiateStoreBill.fulfilled, (state) => {
                state.initiating = false;
            })
            .addCase(initiateStoreBill.rejected, (state) => {
                state.initiating = false;
            });
    },
});

export const { clearStoreBill } = storeBillSlice.actions;
export default storeBillSlice.reducer;